import React from 'react'
import { observer } from 'mobx-react'
import cn from 'classnames'

import ModelStore from 'stores/model/ModelStore'
import Model from 'stores/model/Model'
import TabStore from 'stores/tab/TabStore'
import ProjectStore from 'stores/project/ProjectStore'

import Input from 'components/input/Input'
import Mousetrap from 'components/mousetrap/Mousetrap'

import styles from './modelListTab.scss'

interface IProps {}

type Section = 'recent' | 'all' | 'filtered'
interface IState {
  searchValue: string
  highlightedSection: Section
  highlightedIdx: number
}

class ModelListTab extends React.PureComponent<IProps, IState> {
  state = {
    searchValue: '',
    highlightedSection: 'recent' as Section,
    highlightedIdx: 0, // HighlitedIdx IN highlightedSection
  }

  private input: React.RefObject<HTMLInputElement> = React.createRef()
  private recentModelRefs: React.RefObject<HTMLLIElement>[] = (
    ProjectStore.activeProject?.recentModelIds || []
  ).map(_ => React.createRef())
  private allModelRefs: React.RefObject<HTMLLIElement>[] = Object.keys(
    ModelStore.values
  ).map(_ => React.createRef())
  private filteredModelRefs: React.RefObject<HTMLLIElement>[] = [] // Will be populated when we know what the search term is

  getRecentModels = (): Model[] =>
    ProjectStore.activeProject?.recentModels || []

  getAllModels = (): Model[] => Object.values(ModelStore.values)

  getFilteredModels = (searchValue: string): Model[] => {
    return Object.values(ModelStore.values).filter(m =>
      m.name.toLowerCase().includes(searchValue.toLowerCase())
    )
  }

  scrollToHighlightedIdx = () => {
    const { highlightedSection, highlightedIdx } = this.state

    if (highlightedSection === 'recent') {
      this.recentModelRefs[highlightedIdx]?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    } else if (highlightedSection === 'all') {
      this.allModelRefs[highlightedIdx]?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    } else if (highlightedSection === 'filtered') {
      this.filteredModelRefs[highlightedIdx]?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }

  handleSelectModel = (id: string) => {
    const model = ModelStore.get(id)

    if (!model) {
      return
    }

    ProjectStore.activeProject?.addRecentModel(model.id)

    // Let's try and find a tab to open this model in

    // If this model is already open in another non-dirty tab, switch to it.
    let tab =
      Object.values(TabStore.values).find(
        t =>
          t.session?.isScript &&
          t.session.script.model.id === model.id &&
          t.session.script.frozen
      ) || null
    if (tab) {
      tab.update({ preview: false })
      TabStore.switch({ id: tab.id })
      return
    }

    // If not, try to find a preview tab to open this model in
    tab = TabStore.previewTab
    if (tab) {
      tab.load({
        modelId: model.id,
      })
      tab.update({ preview: false })
      TabStore.switch({ id: tab.id })
      return
    }

    // If all else fails, create a tab
    tab = TabStore.add({
      modelId: model.id,
      preview: false,
    })
    TabStore.switch({ id: tab.id })
  }

  handleSelectHighlightedModel = () => {
    const { highlightedSection, highlightedIdx, searchValue } = this.state

    if (highlightedSection === 'recent') {
      this.handleSelectModel(this.getRecentModels()[highlightedIdx]?.id)
    } else if (highlightedSection === 'all') {
      this.handleSelectModel(this.getAllModels()[highlightedIdx]?.id)
    } else if (highlightedSection === 'filtered') {
      this.handleSelectModel(
        this.getFilteredModels(searchValue)[highlightedIdx]?.id
      )
    }
  }

  handleSearch = (searchValue: string) => {
    this.filteredModelRefs = this.getFilteredModels(searchValue).map(_ =>
      React.createRef()
    )

    const recentModels = ProjectStore.activeProject?.recentModels || []
    if (searchValue === '') {
      this.setState({
        searchValue,
        highlightedSection: recentModels.length > 0 ? 'recent' : 'all',
        highlightedIdx: 0,
      })
    } else {
      this.setState({
        searchValue,
        highlightedSection: 'filtered',
        highlightedIdx: 0,
      })
    }
  }

  handleHighlightPrev = () => {
    this.setState(
      state => {
        let highlightedSection = state.highlightedSection
        let highlightedIdx = state.highlightedIdx

        if (state.highlightedSection === 'recent') {
          if (state.highlightedIdx - 1 < 0) {
            // If you're at the top of the `recent` list, there's nothing to do
          } else {
            // Otherwise, just move up by 1
            highlightedIdx -= 1
          }
        } else if (state.highlightedSection === 'all') {
          if (state.highlightedIdx - 1 < 0) {
            // If you're at the top of the `all` list, move to the `recent` list
            const recentModels = ProjectStore.activeProject?.recentModels || []
            if (recentModels.length === 0) {
              // If there are no recent models, there's nothing to do
            } else {
              // Otherwise, move to the last item in the recent list
              highlightedSection = 'recent'
              highlightedIdx = recentModels.length - 1
            }
          } else {
            // Otherwise, just move up by 1
            highlightedIdx -= 1
          }
        } else if (state.highlightedSection === 'filtered') {
          if (state.highlightedIdx - 1 < 0) {
            // If you're at the top of the `filtered` list, there's nothing to do
          } else {
            // Otherwise, just move up by 1
            highlightedIdx -= 1
          }
        }

        return { highlightedSection, highlightedIdx }
      },
      () => this.scrollToHighlightedIdx()
    )
  }

  handleHighlightNext = () => {
    this.setState(
      state => {
        let highlightedSection = state.highlightedSection
        let highlightedIdx = state.highlightedIdx

        if (state.highlightedSection === 'recent') {
          const recentModels = ProjectStore.activeProject?.recentModels || []
          if (state.highlightedIdx + 1 >= recentModels.length) {
            // If you're at the bottom of the `recent` list, move to the `all` list
            highlightedSection = 'all'
            highlightedIdx = 0
          } else {
            // Otherwise, just move down by 1
            highlightedIdx += 1
          }
        } else if (state.highlightedSection === 'all') {
          const allModels = Object.values(ModelStore.values)
          if (state.highlightedIdx + 1 >= allModels.length) {
            // If you're at the bottom of the `all` list, there's nothing to do
          } else {
            // Otherwise, just move down by 1
            highlightedIdx += 1
          }
        } else if (state.highlightedSection === 'filtered') {
          const filteredModels = this.getFilteredModels(state.searchValue)
          if (state.highlightedIdx + 1 >= filteredModels.length) {
            // If you're at the bottom of the `filtered` list, there's nothing to do
          } else {
            // Otherwise, just move down by 1
            highlightedIdx += 1
          }
        }

        return { highlightedSection, highlightedIdx }
      },
      () => this.scrollToHighlightedIdx()
    )
  }

  render() {
    const { searchValue, highlightedSection, highlightedIdx } = this.state

    const recentModels = ProjectStore.activeProject?.recentModels || []
    const allModels = Object.values(ModelStore.values)
    const filteredModels = this.getFilteredModels(searchValue)

    return (
      <div className={styles.container} data-cy="model-list-tab">
        <div className={styles.content}>
          <header className={styles.header}>
            <div className={styles.title}>Open a Model</div>

            <Input
              data-cy="search"
              innerRef={this.input}
              type="text"
              className={styles.search}
              focusOnMount
              value={searchValue}
              onChange={this.handleSearch}
              placeholder="Search"
            />
          </header>

          {recentModels.length > 0 && searchValue === '' && (
            <section data-cy="recent-model-list" className={styles.section}>
              <div className={styles.sectionTitle}>Recently Opened</div>
              <ul className={styles.list}>
                {recentModels.map((m, idx) => (
                  <li
                    key={m.id}
                    ref={this.recentModelRefs[idx]}
                    className={cn(styles.model, {
                      [styles.active]:
                        highlightedSection === 'recent' &&
                        highlightedIdx === idx,
                    })}
                    onClick={_ => this.handleSelectModel(m.id)}
                  >
                    <div data-cy="name" className={styles.name}>
                      {m.name}
                    </div>
                    <div className={styles.spacer}></div>
                    <div data-cy="count" className={styles.count}>
                      {m.count ?? ''}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {searchValue === '' && (
            <section
              data-cy="model-list"
              className={cn(styles.section, styles.allModels)}
            >
              <div className={styles.sectionTitle}>All Models</div>
              <ul className={styles.list}>
                {allModels.map((m, idx) => (
                  <li
                    key={m.id}
                    ref={this.allModelRefs[idx]}
                    className={cn(styles.model, {
                      [styles.active]:
                        highlightedSection === 'all' && highlightedIdx === idx,
                    })}
                    onClick={_ => this.handleSelectModel(m.id)}
                  >
                    <div data-cy="name" className={styles.name}>
                      {m.name}
                    </div>
                    <div className={styles.spacer}></div>
                    <div data-cy="count" className={styles.count}>
                      {m.count ?? ''}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {searchValue !== '' && (
            <section
              data-cy="filtered-model-list"
              className={cn(styles.section, styles.allModels)}
            >
              <div className={styles.sectionTitle}>Matches</div>
              <ul className={styles.list}>
                {filteredModels.map((m, idx) => (
                  <li
                    key={m.id}
                    ref={this.filteredModelRefs[idx]}
                    className={cn(styles.model, {
                      [styles.active]:
                        highlightedSection === 'filtered' &&
                        highlightedIdx === idx,
                    })}
                    onClick={_ => {
                      this.handleSelectModel(m.id)
                      TabStore.activeTab?.update({ preview: false })
                    }}
                  >
                    <div data-cy="name" className={styles.name}>
                      {m.name}
                    </div>
                    <div className={styles.spacer}></div>
                    <div data-cy="count" className={styles.count}>
                      {m.count ?? ''}
                    </div>
                  </li>
                ))}
              </ul>

              {filteredModels.length === 0 && (
                <div className={styles.empty}>No matches</div>
              )}
            </section>
          )}
        </div>
        <Mousetrap
          keys={['command+shift+[', 'command+option+right']}
          target={this.input}
          onMatch={() => TabStore.switch({ direction: 'right' })}
        />
        <Mousetrap
          keys={['command+shift+]', 'command+option+left']}
          target={this.input}
          onMatch={() => TabStore.switch({ direction: 'left' })}
        />
        <Mousetrap
          keys="up"
          target={this.input}
          onMatch={() => this.handleHighlightPrev()}
        />
        <Mousetrap
          keys="down"
          target={this.input}
          onMatch={() => this.handleHighlightNext()}
        />
        <Mousetrap
          keys="enter"
          target={this.input}
          onMatch={() => this.handleSelectHighlightedModel()}
        />
        <Mousetrap
          keys="cmd+enter"
          target={this.input}
          onMatch={() => this.handleSelectHighlightedModel()}
        />
      </div>
    )
  }
}

export default observer(ModelListTab)
